import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Alert, Button, ReportDateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'helpers/dayjs';
import { FiRefreshCw, FiFilter, FiUsers, FiBriefcase, FiGlobe, FiUserCheck } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';

import WaiverSummaryCards   from './WaiverSummaryCards';
import WaiverStatusChart    from './WaiverStatusChart';
import WaiverTrendChart     from './WaiverTrendChart';
import WaiverGroupBarCard   from './WaiverGroupBarCard';
import WaiverBreakdownChart from './WaiverBreakdownChart';
import WaiverOverdueCard    from './WaiverOverdueCard';
import WaiverDetailedTable  from './WaiverDetailedTable';
import WaiverFilterSidebar  from './WaiverFilterSidebar';
import WaiverDrillDownPanel from './WaiverDrillDownPanel';

import {
  fetchWaiverSummary,
  fetchWaiverTrend,
  fetchWaiverStatus,
  fetchWaiverOverdue,
  fetchWaiverCseIncharge,
  fetchWaiverSalesUnit,
  fetchWaiverRegion,
  fetchWaiverSalesIncharge,
  fetchWaiverDetailed,
  fetchWaiverFilterOptions,
} from 'redux/crm/dashboard/waiverSlice';

const DEFAULT_START = dayjs().startOf('year').format('YYYY-MM-DD');
const DEFAULT_END   = dayjs().format('YYYY-MM-DD');

// Sticks right below the Falcon top navbar (which is itself position: sticky)
const STICKY_BELOW_NAVBAR = { top: 'var(--falcon-top-nav-height, 69px)' };

const WaiverDashboard = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('start_date') || DEFAULT_START,
    endDate:   searchParams.get('end_date')   || DEFAULT_END,
  });

  const [filters, setFilters] = useState({
    sales_units:     searchParams.get('sales_units')?.split(',').filter(Boolean).map(Number) || [],
    statuses:        searchParams.get('statuses')?.split(',').filter(Boolean).map(Number) || [],
    cse_incharges:   searchParams.get('cse_incharges')?.split(',').filter(Boolean) || [],
    sales_incharges: searchParams.get('sales_incharges')?.split(',').filter(Boolean) || [],
    overdue:         searchParams.get('overdue') === 'true',
    commitment_from: searchParams.get('commitment_from') || '',
    commitment_to:   searchParams.get('commitment_to')   || '',
  });

  const [groupBy, setGroupBy]         = useState(searchParams.get('group_by') || 'month');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Detailed table — server-side pagination / search / sort / export
  const [detailedPage, setDetailedPage]   = useState(1);
  const [detailedPer, setDetailedPer]     = useState(50);
  const [tableSearch, setTableSearch]     = useState('');
  const [tableSort, setTableSort]         = useState({ by: 'created_at', dir: 'DESC' });
  const [exportLoading, setExportLoading] = useState(false);

  const [drillDown, setDrillDown] = useState({ open: false, title: '', extraParams: {} });

  const {
    summary, trend, status, overdue, cseIncharge,
    salesUnit, region, salesIncharge, detailed, filterOptions,
  } = useSelector(state => state.crm_dashboard_waiver);

  const buildParams = useCallback((extra = {}) => {
    const p = {
      start_date: dateRange.startDate,
      end_date:   dateRange.endDate,
      ...extra,
    };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units;
    if (filters.statuses.length)        p.statuses        = filters.statuses;
    if (filters.cse_incharges.length)   p.cse_incharges   = filters.cse_incharges;
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges;
    if (filters.overdue)                p.overdue         = 'true';
    if (filters.commitment_from)        p.commitment_from = filters.commitment_from;
    if (filters.commitment_to)          p.commitment_to   = filters.commitment_to;
    return p;
  }, [dateRange, filters]);

  const baseParams = useMemo(() => buildParams(), [buildParams]);

  const detailedParams = useMemo(() => ({
    ...baseParams,
    page:     detailedPage,
    per_page: detailedPer,
    q:        tableSearch || undefined,
    sort_by:  tableSort.by,
    sort_dir: tableSort.dir,
  }), [baseParams, detailedPage, detailedPer, tableSearch, tableSort]);

  // Sync URL params so filtered views are shareable
  useEffect(() => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate, group_by: groupBy };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units.join(',');
    if (filters.statuses.length)        p.statuses        = filters.statuses.join(',');
    if (filters.cse_incharges.length)   p.cse_incharges   = filters.cse_incharges.join(',');
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges.join(',');
    if (filters.overdue)                p.overdue         = 'true';
    if (filters.commitment_from)        p.commitment_from = filters.commitment_from;
    if (filters.commitment_to)          p.commitment_to   = filters.commitment_to;
    setSearchParams(p);
  }, [dateRange, filters, groupBy, setSearchParams]);

  // Fetch all sections whenever filters change
  useEffect(() => {
    dispatch(fetchWaiverSummary(baseParams));
    dispatch(fetchWaiverStatus(baseParams));
    dispatch(fetchWaiverOverdue(baseParams));
    dispatch(fetchWaiverCseIncharge(baseParams));
    dispatch(fetchWaiverSalesUnit(baseParams));
    dispatch(fetchWaiverRegion(baseParams));
    dispatch(fetchWaiverSalesIncharge(baseParams));
    dispatch(fetchWaiverFilterOptions({ sales_units: filters.sales_units }));
  }, [dispatch, baseParams, filters.sales_units]);

  // Trend fetches separately (depends on groupBy)
  useEffect(() => {
    dispatch(fetchWaiverTrend({ ...baseParams, group_by: groupBy }));
  }, [dispatch, baseParams, groupBy]);

  // Detailed table fetches separately (page / search / sort)
  useEffect(() => {
    dispatch(fetchWaiverDetailed(detailedParams));
  }, [dispatch, detailedParams]);

  const handleApplyFilters = (next) => {
    setFilters(next);
    setDetailedPage(1);
    setSidebarOpen(false);
  };

  const handleResetFilters = () => {
    setFilters({
      sales_units: [], statuses: [], cse_incharges: [], sales_incharges: [],
      overdue: false, commitment_from: '', commitment_to: '',
    });
    setDetailedPage(1);
  };

  const handleRefresh = () => {
    dispatch(fetchWaiverSummary(baseParams));
    dispatch(fetchWaiverTrend({ ...baseParams, group_by: groupBy }));
    dispatch(fetchWaiverStatus(baseParams));
    dispatch(fetchWaiverOverdue(baseParams));
    dispatch(fetchWaiverCseIncharge(baseParams));
    dispatch(fetchWaiverSalesUnit(baseParams));
    dispatch(fetchWaiverRegion(baseParams));
    dispatch(fetchWaiverSalesIncharge(baseParams));
    dispatch(fetchWaiverDetailed(detailedParams));
  };

  // ── Drill-down helpers ─────────────────────────────────────────────────────
  const openDrillDown = (title, extraParams) => setDrillDown({ open: true, title, extraParams });

  const handleTrendDrill = (row, gb) => {
    const start = dayjs(row.period);
    const end   = dayjs.min(start.clone().endOf(gb), dayjs());
    openDrillDown(`Period: ${row.label}`, {
      start_date: start.format('YYYY-MM-DD'),
      end_date:   end.format('YYYY-MM-DD'),
    });
  };

  // sales_unit_stats groups by name — resolve the id from filter options
  const handleUnitDrill = (row) => {
    const unit = (filterOptions.data?.sales_units || []).find(u => u.name === row.unit_name);
    openDrillDown(`Sales Unit: ${row.unit_name}`, unit ? { sales_units: [unit.id] } : { region: row.region });
  };

  // ── Server-side Excel export (current filters + search + sort) ────────────
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/waivers/export_detailed_data', {
        params: {
          ...baseParams,
          q:        tableSearch || undefined,
          sort_by:  tableSort.by,
          sort_dir: tableSort.dir,
        },
        responseType: 'blob',
      });

      // Guard: if the server answered with JSON/HTML (error body), don't save
      // it as .xlsx — that produces a file Excel cannot open.
      const contentType = res.data?.type || '';
      if (contentType.includes('json') || contentType.includes('html')) {
        const text = await res.data.text();
        let message = 'Export failed.';
        try { message = JSON.parse(text)?.error || message; } catch { /* keep default */ }
        window.alert(message);
        return;
      }

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url;
      a.download = `waivers-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Error responses also arrive as blobs when responseType is 'blob'
      let message = 'Export failed. Please try again.';
      try {
        const text = await e?.response?.data?.text?.();
        if (text) message = JSON.parse(text)?.error || message;
      } catch { /* keep default */ }
      window.alert(message);
    } finally {
      setExportLoading(false);
    }
  };

  const activeFilterCount =
    filters.sales_units.length + filters.statuses.length +
    filters.cse_incharges.length + filters.sales_incharges.length +
    (filters.overdue ? 1 : 0) +
    (filters.commitment_from || filters.commitment_to ? 1 : 0);

  const anyError = summary.error || status.error;

  return (
    <div className="tw-min-h-screen tw-bg-slate-50">
      {/* ── Sticky header (pinned below the app navbar) ── */}
      <div className="tw-sticky tw-z-30 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm" style={STICKY_BELOW_NAVBAR}>
        <div className="tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <div className="tw-flex-1 tw-min-w-0">
            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-0">Waiver Dashboard</h1>
            <p className="tw-text-xs tw-text-gray-400 tw-hidden md:tw-block tw-mb-0">
              Status · Overdue · CSE Handle · Sales Unit · Region · Sales Incharge
            </p>
          </div>

          <ReportDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={({ startDate, endDate }) => { setDateRange({ startDate, endDate }); setDetailedPage(1); }}
          />

          <Button onClick={() => setSidebarOpen(o => !o)} variant="secondary">
            <FiFilter size={14} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-primary-600 tw-text-white tw-text-xs tw-font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>

          <Button
            onClick={handleRefresh}
            variant="ghost"
            className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-500 hover:tw-text-gray-800"
          >
            <FiRefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {anyError && (
        <div className="tw-px-5 tw-pt-4">
          <Alert type="error" message="Some sections failed to load. Try refreshing." dismissible />
        </div>
      )}

      {/* ── Main layout: sidebar + content ── */}
      <div className="tw-flex tw-gap-5 tw-px-5 tw-py-5">

        {/* Filter sidebar */}
        <div className={`tw-flex-shrink-0 tw-w-64 tw-transition-all tw-duration-300 ${sidebarOpen ? 'tw-block' : 'tw-hidden md:tw-block'}`}>
          <div className="tw-sticky" style={{ top: 'calc(var(--falcon-top-nav-height, 69px) + 76px)' }}>
            <WaiverFilterSidebar
              filterOptions={filterOptions.data}
              loading={filterOptions.loading}
              filters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>
        </div>

        {/* Dashboard content */}
        <div className="tw-flex-1 tw-min-w-0 tw-space-y-5">

          {/* 1. KPI summary — each card drills down to its record subset */}
          <WaiverSummaryCards data={summary.data} loading={summary.loading} onDrillDown={openDrillDown} />

          {/* 2. Trend by month / quarter / year — click a column for details */}
          <WaiverTrendChart
            data={trend.data}
            loading={trend.loading}
            error={trend.error}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            onDrillDown={handleTrendDrill}
          />

          {/* 3. Status breakdown + Top CSE handle */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <WaiverStatusChart
              data={status.data}
              loading={status.loading}
              error={status.error}
              onDrillDown={openDrillDown}
            />
            <WaiverBreakdownChart
              title="CSE Handle"
              Icon={FiUserCheck}
              iconColor="tw-text-violet-500"
              personLabel="CSE"
              labelKey="cse_incharge"
              drillKey="cse_incharges"
              drillTitlePrefix="CSE"
              data={cseIncharge.data}
              loading={cseIncharge.loading}
              error={cseIncharge.error}
              onItemClick={openDrillDown}
              fullHeight
            />
          </div>

          {/* 4. Overdue analysis — click an aging bucket for details */}
          <WaiverOverdueCard
            data={overdue.data}
            loading={overdue.loading}
            error={overdue.error}
            onDrillDown={openDrillDown}
          />

          {/* 5. Sales unit + Region */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <WaiverGroupBarCard
              title="By Sales Unit"
              Icon={FiBriefcase}
              iconColor="tw-text-blue-500"
              data={salesUnit.data}
              loading={salesUnit.loading}
              error={salesUnit.error}
              labelKey="unit_name"
              subLabelKey="region"
              onItemClick={handleUnitDrill}
            />
            <WaiverGroupBarCard
              title="By Sales Region"
              Icon={FiGlobe}
              iconColor="tw-text-emerald-500"
              data={region.data}
              loading={region.loading}
              error={region.error}
              labelKey="region"
              onItemClick={(r) => openDrillDown(`Region: ${r.region}`, { region: r.region })}
            />
          </div>

          {/* 6. Sales incharge — Chart/Table views with metric toggle */}
          <WaiverBreakdownChart
            title="By Sales Incharge"
            Icon={FiUsers}
            iconColor="tw-text-amber-500"
            personLabel="Sales Incharge"
            labelKey="sales_incharge"
            drillKey="sales_incharges"
            drillTitlePrefix="Sales Incharge"
            data={salesIncharge.data}
            loading={salesIncharge.loading}
            error={salesIncharge.error}
            onItemClick={openDrillDown}
          />

          {/* 7. Detailed records — server-side search / sort / pagination / export */}
          <WaiverDetailedTable
            rows={detailed.rows}
            totalRows={detailed.totalRows}
            page={detailed.page}
            perPage={detailedPer}
            loading={detailed.loading}
            error={detailed.error}
            search={tableSearch}
            sortBy={tableSort.by}
            sortDir={tableSort.dir}
            onPageChange={setDetailedPage}
            onPerPageChange={(n) => { setDetailedPer(n); setDetailedPage(1); }}
            onSearchChange={(q) => { setTableSearch(q); setDetailedPage(1); }}
            onSortChange={(by, dir) => { setTableSort({ by, dir }); setDetailedPage(1); }}
            onExport={handleExportExcel}
            exportLoading={exportLoading}
          />
        </div>
      </div>

      {/* Drill-down slide-over */}
      <WaiverDrillDownPanel
        open={drillDown.open}
        title={drillDown.title}
        baseParams={baseParams}
        extraParams={drillDown.extraParams}
        onClose={() => setDrillDown(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default WaiverDashboard;
